<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const fixedFields = [
  'first_name',
  'last_name',
  'email',
  'password',
  'birth_date',
  'phone',
  'gender',
  'nationality',
] as const

type FixedFieldKey = (typeof fixedFields)[number]

const fieldTypeLabels: Record<FixedFieldKey, string> = {
  first_name: 'Text',
  last_name: 'Text',
  email: 'Email',
  password: 'Password',
  birth_date: 'Date',
  phone: 'Phone',
  gender: 'Select',
  nationality: 'Select',
}
</script>

<template>
  <div class="bg-slate-800/50 border border-amber-500/30 rounded-2xl p-5">
    <!-- Heading -->
    <div class="flex items-center gap-3 mb-2">
      <svg
        class="w-5 h-5 text-amber-400 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
      <h2 class="text-base font-semibold text-white">
        {{ t('wizard_forms.fixed_page.title') }}
      </h2>
      <span class="ml-auto inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/40 px-2.5 py-0.5 text-xs font-medium text-amber-300">
        {{ t('wizard_forms.fixed_page.badge') }}
      </span>
    </div>

    <!-- Description -->
    <p class="mb-5 text-sm text-slate-400 leading-relaxed">
      {{ t('wizard_forms.fixed_page.description') }}
    </p>

    <!-- Field cards grid -->
    <div class="grid grid-cols-2 gap-2.5">
      <div
        v-for="field in fixedFields"
        :key="field"
        class="flex flex-col gap-1 rounded-lg border border-slate-700/80 bg-slate-900/40 px-3 py-2.5"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {{ fieldTypeLabels[field] }}
          </span>
          <span class="inline-flex items-center rounded-full bg-slate-700/60 border border-slate-600/60 px-1.5 py-0.5 text-xs font-medium text-slate-400">
            Required
          </span>
        </div>
        <span class="text-sm font-medium text-slate-300">
          {{ t(`wizard_forms.fixed_page.fields.${field}`) }}
        </span>
      </div>
    </div>
  </div>
</template>
